import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, Briefcase, FileText, LogOut, Download, Plus,
  ExternalLink, Edit3, Trash2, Eye, EyeOff, Search, Loader2, Star, UserCog,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { downloadCSV, formatDate } from "@/lib/csv";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import JobFormDialog from "@/components/admin/JobFormDialog";
import FreelancerFormDialog from "@/components/admin/FreelancerFormDialog";
import Logo from "@/components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "leads", label: "Leads", icon: FileText },
  { key: "applications", label: "Applications", icon: Briefcase },
  { key: "candidates", label: "Candidates", icon: Users },
  { key: "jobs", label: "Jobs", icon: Briefcase },
  { key: "freelancers", label: "Freelancers", icon: UserCog },
];

export default function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState("overview");

  const handleLogout = () => {
    logout();
    toast.success("Signed out");
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Logo variant="light" to="/" />
            <span className="hidden md:inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-primary/10 text-primary text-[10px] uppercase tracking-wider font-bold">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right text-xs">
              <div className="font-semibold text-slate-900">{user?.name}</div>
              <div className="text-slate-500">{user?.email}</div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              data-testid="admin-logout"
              className="gap-2"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 lg:py-8 grid lg:grid-cols-[220px_1fr] gap-6">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-20 self-start">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActive(key)}
                data-testid={`admin-tab-${key}`}
                className={`inline-flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  active === key
                    ? "bg-primary text-white shadow-sm"
                    : "bg-white text-slate-700 hover:bg-slate-100 border border-slate-200"
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.75} />
                {label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <main className="min-w-0">
          {active === "overview" && <Overview />}
          {active === "leads" && <LeadsTable />}
          {active === "applications" && <ApplicationsTable />}
          {active === "candidates" && <CandidatesTable />}
          {active === "jobs" && <JobsTable />}
          {active === "freelancers" && <FreelancersTable />}
        </main>
      </div>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/stats`).then((r) => setStats(r.data)).catch((e) => setErr(e.message));
  }, []);

  if (err) return <ErrorBlock msg={err} />;
  if (!stats) return <Loading />;

  const cards = [
    { label: "Leads", value: stats.leads, recent: stats.recent_7d.leads, icon: FileText },
    { label: "Applications", value: stats.applications, recent: stats.recent_7d.applications, icon: Briefcase },
    { label: "Candidates", value: stats.candidates, recent: stats.recent_7d.candidates, icon: Users },
    { label: "Active Jobs", value: stats.jobs_active, sub: `${stats.jobs_draft} drafts`, icon: Briefcase },
  ];

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <PageHeader title="Dashboard overview" subtitle="Snapshot of leads, candidates and openings." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(({ label, value, recent, sub, icon: Icon }) => (
          <div key={label} className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</div>
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon className="w-4 h-4 text-primary" strokeWidth={1.6} />
              </div>
            </div>
            <div className="mt-3 font-heading font-extrabold text-3xl text-slate-900">{value}</div>
            <div className="mt-1 text-xs text-slate-500">
              {recent != null ? `${recent} new in last 7 days` : sub}
            </div>
          </div>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <h3 className="font-heading font-bold text-slate-900">Quick actions</h3>
        <p className="text-sm text-slate-600 mt-1">Manage your pipeline.</p>
        <div className="mt-4 flex flex-wrap gap-3">
          <a
            href="/careers"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50"
          >
            View Careers page <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </div>
  );
}

// ---------- Leads ----------
function LeadsTable() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/leads`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((it) =>
      [it.full_name, it.email, it.phone, it.company, it.service_interest, it.message].join(" ").toLowerCase().includes(s)
    );
  }, [items, q]);

  const handleExport = () => {
    downloadCSV(`leads-${new Date().toISOString().slice(0, 10)}.csv`, [
      { key: "created_at", label: "Created", value: (r) => formatDate(r.created_at) },
      { key: "full_name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "company", label: "Company" },
      { key: "service_interest", label: "Service" },
      { key: "employees_needed", label: "Employees" },
      { key: "message", label: "Message" },
      { key: "source", label: "Source" },
    ], filtered);
  };

  return (
    <div data-testid="admin-leads">
      <PageHeader
        title="Leads"
        subtitle="Inbound enquiries from corporate clients."
        right={
          <Button
            onClick={handleExport}
            data-testid="admin-leads-export"
            disabled={!filtered.length}
            className="gap-2 bg-slate-900 hover:bg-primary text-white"
          >
            <Download className="w-4 h-4" /> Export CSV ({filtered.length})
          </Button>
        }
      />
      <TableShell search={q} onSearch={setQ} placeholder="Search by name, email, company…" testId="admin-leads-search">
        {items === null ? <Loading /> : filtered.length === 0 ? <Empty msg="No leads yet." /> : (
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <Th>Date</Th><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Company</Th><Th>Service</Th><Th>Employees</Th><Th>Message</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((l) => (
                <tr key={l.id} data-testid={`admin-lead-row-${l.id}`} className="hover:bg-slate-50">
                  <Td className="text-slate-500 whitespace-nowrap">{formatDate(l.created_at)}</Td>
                  <Td className="font-medium text-slate-900">{l.full_name}</Td>
                  <Td><a className="text-primary hover:underline" href={`mailto:${l.email}`}>{l.email}</a></Td>
                  <Td><a className="text-slate-700 hover:text-primary" href={`tel:${l.phone}`}>{l.phone}</a></Td>
                  <Td>{l.company || "—"}</Td>
                  <Td>{l.service_interest || "—"}</Td>
                  <Td>{l.employees_needed || "—"}</Td>
                  <Td className="max-w-[300px] truncate" title={l.message}>{l.message || "—"}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </TableShell>
    </div>
  );
}

// ---------- Applications ----------
function ApplicationsTable() {
  const [items, setItems] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState("");
  const [jobFilter, setJobFilter] = useState("all");

  useEffect(() => {
    axios.get(`${API}/admin/jobs`).then((r) => setJobs(r.data)).catch(() => setJobs([]));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (jobFilter !== "all") params.set("job_id", jobFilter);
    axios.get(`${API}/admin/applications?${params.toString()}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [jobFilter]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((it) =>
      [it.full_name, it.email, it.phone, it.job_title, it.current_company, it.current_city, it.qualification].join(" ").toLowerCase().includes(s)
    );
  }, [items, q]);

  const downloadResume = async (appId, filename) => {
    try {
      const res = await axios.get(`${API}/applications/${appId}/resume`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename || "resume";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download resume");
    }
  };

  const handleExport = () => {
    downloadCSV(`applications-${new Date().toISOString().slice(0, 10)}.csv`, [
      { key: "created_at", label: "Applied", value: (r) => formatDate(r.created_at) },
      { key: "job_title", label: "Job" },
      { key: "full_name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "current_city", label: "City" },
      { key: "qualification", label: "Qualification" },
      { key: "total_experience", label: "Experience" },
      { key: "current_salary", label: "Current CTC" },
      { key: "expected_salary", label: "Expected CTC" },
      { key: "current_company", label: "Current Company" },
      { key: "notice_period", label: "Notice" },
      { key: "resume_filename", label: "Resume" },
      { key: "cover_letter", label: "Cover Letter" },
    ], filtered);
  };

  return (
    <div data-testid="admin-applications">
      <PageHeader
        title="Job Applications"
        subtitle="Candidates who applied to your live openings."
        right={
          <Button onClick={handleExport} data-testid="admin-applications-export" disabled={!filtered.length} className="gap-2 bg-slate-900 hover:bg-primary text-white">
            <Download className="w-4 h-4" /> Export CSV ({filtered.length})
          </Button>
        }
      />
      <TableShell search={q} onSearch={setQ} placeholder="Search by name, email, job, city…" testId="admin-applications-search"
        rightSlot={
          <select
            value={jobFilter}
            onChange={(e) => setJobFilter(e.target.value)}
            data-testid="admin-applications-job-filter"
            className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-700 max-w-[260px]"
          >
            <option value="all">All jobs</option>
            {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
          </select>
        }
      >
        {items === null ? <Loading /> : filtered.length === 0 ? <Empty msg="No applications yet." /> : (
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <Th>Date</Th><Th>Name</Th><Th>Job</Th><Th>Email</Th><Th>Phone</Th><Th>City</Th><Th>Qualification</Th><Th>Experience</Th><Th>Current / Expected</Th><Th>Status</Th><Th>Commission</Th><Th>Resume</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((a) => (
                <tr key={a.id} data-testid={`admin-app-row-${a.id}`} className="hover:bg-slate-50">
                  <Td className="text-slate-500 whitespace-nowrap">{formatDate(a.created_at)}</Td>
                  <Td className="font-medium text-slate-900">{a.full_name}</Td>
                  <Td className="text-slate-700 max-w-[180px] truncate" title={a.job_title}>{a.job_title}</Td>
                  <Td><a className="text-primary hover:underline" href={`mailto:${a.email}`}>{a.email}</a></Td>
                  <Td><a className="text-slate-700 hover:text-primary" href={`tel:${a.phone}`}>{a.phone}</a></Td>
                  <Td>{a.current_city || "—"}</Td>
                  <Td className="max-w-[160px] truncate" title={a.qualification}>{a.qualification || "—"}</Td>
                  <Td>{a.total_experience || "—"}</Td>
                  <Td className="whitespace-nowrap text-xs">
                    {a.current_salary || "—"} <span className="text-slate-300">→</span> {a.expected_salary || "—"}
                  </Td>
                  <Td>
                    <select
                      value={a.status || "submitted"}
                      onChange={async (e) => {
                        const newStatus = e.target.value;
                        try {
                          await axios.patch(`${API}/admin/applications/${a.id}/status`, { status: newStatus });
                          setItems((prev) => prev.map((x) => x.id === a.id ? { ...x, status: newStatus } : x));
                          toast.success("Status updated");
                        } catch { toast.error("Could not update status"); }
                      }}
                      data-testid={`admin-app-status-${a.id}`}
                      className="h-8 text-xs px-2 rounded-md border border-slate-200 bg-white"
                    >
                      {["submitted","shortlisted","interview","selected","joined","rejected"].map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </Td>
                  <Td>
                    <button
                      type="button"
                      onClick={async () => {
                        const next = a.commission_status === "paid" ? "pending" : "paid";
                        try {
                          await axios.patch(`${API}/admin/applications/${a.id}/commission`, { commission_status: next });
                          setItems((prev) => prev.map((x) => x.id === a.id ? { ...x, commission_status: next } : x));
                          toast.success(`Commission marked ${next}`);
                        } catch { toast.error("Could not update commission"); }
                      }}
                      data-testid={`admin-app-commission-${a.id}`}
                      className={`inline-flex items-center px-2 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider border ${
                        a.commission_status === "paid"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-100"
                          : "bg-orange-50 text-orange-700 border-orange-100 hover:bg-orange-100"
                      }`}
                      title="Click to toggle Pending / Paid"
                    >
                      {a.commission_status || "pending"}
                    </button>
                  </Td>
                  <Td>
                    <button
                      type="button"
                      onClick={() => downloadResume(a.id, a.resume_filename)}
                      data-testid={`admin-app-resume-${a.id}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary-hover"
                    >
                      <Download className="w-3.5 h-3.5" /> Resume
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </TableShell>
    </div>
  );
}

// ---------- Candidates ----------
function CandidatesTable() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/candidates`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((it) =>
      [it.full_name, it.email, it.phone, it.skills, it.current_role, it.location].join(" ").toLowerCase().includes(s)
    );
  }, [items, q]);

  const downloadResume = async (cid, filename) => {
    try {
      const res = await axios.get(`${API}/candidates/${cid}/resume`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename || "resume";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download resume");
    }
  };

  const handleExport = () => {
    downloadCSV(`candidates-${new Date().toISOString().slice(0, 10)}.csv`, [
      { key: "created_at", label: "Registered", value: (r) => formatDate(r.created_at) },
      { key: "full_name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "location", label: "Location" },
      { key: "current_role", label: "Current Role" },
      { key: "experience_years", label: "Experience" },
      { key: "expected_ctc", label: "Expected CTC" },
      { key: "skills", label: "Skills" },
      { key: "summary", label: "Summary" },
      { key: "resume_filename", label: "Resume" },
    ], filtered);
  };

  return (
    <div data-testid="admin-candidates">
      <PageHeader
        title="Talent Pool"
        subtitle="Candidates who registered their profile."
        right={
          <Button onClick={handleExport} data-testid="admin-candidates-export" disabled={!filtered.length} className="gap-2 bg-slate-900 hover:bg-primary text-white">
            <Download className="w-4 h-4" /> Export CSV ({filtered.length})
          </Button>
        }
      />
      <TableShell search={q} onSearch={setQ} placeholder="Search by name, skill, role…" testId="admin-candidates-search">
        {items === null ? <Loading /> : filtered.length === 0 ? <Empty msg="No candidates yet." /> : (
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <Th>Date</Th><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Role</Th><Th>Exp</Th><Th>Location</Th><Th>Resume</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((c) => (
                <tr key={c.id} data-testid={`admin-cand-row-${c.id}`} className="hover:bg-slate-50">
                  <Td className="text-slate-500 whitespace-nowrap">{formatDate(c.created_at)}</Td>
                  <Td className="font-medium text-slate-900">{c.full_name}</Td>
                  <Td><a className="text-primary hover:underline" href={`mailto:${c.email}`}>{c.email}</a></Td>
                  <Td><a className="text-slate-700 hover:text-primary" href={`tel:${c.phone}`}>{c.phone}</a></Td>
                  <Td>{c.current_role || "—"}</Td>
                  <Td>{c.experience_years || "—"}</Td>
                  <Td>{c.location || "—"}</Td>
                  <Td>
                    <button
                      type="button"
                      onClick={() => downloadResume(c.id, c.resume_filename)}
                      data-testid={`admin-cand-resume-${c.id}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary-hover"
                    >
                      <Download className="w-3.5 h-3.5" /> Resume
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </TableShell>
    </div>
  );
}

// ---------- Jobs ----------
function JobsTable() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // job or {} for new
  const [open, setOpen] = useState(false);

  const refresh = () => {
    axios.get(`${API}/admin/jobs`).then((r) => setItems(r.data)).catch(() => setItems([]));
  };
  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((it) =>
      [it.title, it.department, it.location, it.skills?.join(" ")].join(" ").toLowerCase().includes(s)
    );
  }, [items, q]);

  const handleToggle = async (job) => {
    try {
      await axios.patch(`${API}/admin/jobs/${job.id}/toggle`);
      toast.success(`Job ${job.is_active ? "moved to draft" : "published"}`);
      refresh();
    } catch {
      toast.error("Could not update job");
    }
  };

  const handleFeatured = async (job) => {
    try {
      await axios.patch(`${API}/admin/jobs/${job.id}/featured`);
      toast.success(job.is_featured ? "Removed from featured" : "Marked as featured");
      refresh();
    } catch {
      toast.error("Could not update featured");
    }
  };

  const handleDelete = async (job) => {
    if (!window.confirm(`Delete "${job.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/jobs/${job.id}`);
      toast.success("Job deleted");
      refresh();
    } catch {
      toast.error("Could not delete job");
    }
  };

  return (
    <div data-testid="admin-jobs">
      <PageHeader
        title="Jobs"
        subtitle="Publish openings to your Careers page."
        right={
          <Button
            onClick={() => { setEditing(null); setOpen(true); }}
            data-testid="admin-jobs-create"
            className="gap-2 bg-primary hover:bg-primary-hover text-white"
          >
            <Plus className="w-4 h-4" /> Post a Job
          </Button>
        }
      />
      <TableShell search={q} onSearch={setQ} placeholder="Search jobs…" testId="admin-jobs-search">
        {items === null ? <Loading /> : filtered.length === 0 ? <Empty msg="No jobs yet. Click 'Post a Job' to create one." /> : (
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <Th>Title</Th><Th>Department</Th><Th>Location</Th><Th>Type</Th><Th>Openings</Th><Th>Status</Th><Th>Posted</Th><Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((j) => (
                <tr key={j.id} data-testid={`admin-job-row-${j.id}`} className="hover:bg-slate-50">
                  <Td className="font-medium text-slate-900">{j.title}</Td>
                  <Td>{j.department}</Td>
                  <Td>{j.location}</Td>
                  <Td>{j.employment_type}</Td>
                  <Td>{j.openings}</Td>
                  <Td>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold ${
                      j.is_active ? "bg-emerald-50 text-emerald-700 border border-emerald-100" : "bg-slate-100 text-slate-600 border border-slate-200"
                    }`}>
                      {j.is_active ? "Active" : "Draft"}
                    </span>
                  </Td>
                  <Td className="text-slate-500 whitespace-nowrap">{formatDate(j.posted_at)}</Td>
                  <Td className="text-right whitespace-nowrap">
                    <button
                      onClick={() => handleFeatured(j)}
                      data-testid={`admin-job-featured-${j.id}`}
                      title={j.is_featured ? "Remove featured" : "Mark featured"}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold hover:bg-slate-100 ${
                        j.is_featured ? "text-primary" : "text-slate-500"
                      }`}
                    >
                      <Star className={`w-3.5 h-3.5 ${j.is_featured ? "fill-current" : ""}`} />
                    </button>
                    <button
                      onClick={() => handleToggle(j)}
                      data-testid={`admin-job-toggle-${j.id}`}
                      className="ml-1 inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold text-slate-700 hover:bg-slate-100"
                      title={j.is_active ? "Move to draft" : "Publish"}
                    >
                      {j.is_active ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      onClick={() => { setEditing(j); setOpen(true); }}
                      data-testid={`admin-job-edit-${j.id}`}
                      className="ml-1 inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold text-slate-700 hover:bg-slate-100"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(j)}
                      data-testid={`admin-job-delete-${j.id}`}
                      className="ml-1 inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold text-destructive hover:bg-red-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </TableShell>

      <JobFormDialog
        open={open}
        onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}
        job={editing}
        onSaved={() => { refresh(); setOpen(false); setEditing(null); }}
      />
    </div>
  );
}

// ---------- Freelancers ----------
function FreelancersTable() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const refresh = () => {
    axios.get(`${API}/admin/users?role=freelancer`)
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  };
  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((u) => [u.name, u.email, u.mobile].join(" ").toLowerCase().includes(s));
  }, [items, q]);

  const handleToggle = async (u) => {
    try {
      await axios.patch(`${API}/admin/users/${u.id}/toggle`);
      toast.success(u.is_active ? "Freelancer disabled" : "Freelancer enabled");
      refresh();
    } catch { toast.error("Could not update"); }
  };

  return (
    <div data-testid="admin-freelancers">
      <PageHeader
        title="Freelancers"
        subtitle="Add and manage freelancer accounts. Disabled users cannot log in."
        right={
          <Button
            onClick={() => { setEditing(null); setOpen(true); }}
            data-testid="admin-freelancers-create"
            className="gap-2 bg-primary hover:bg-primary-hover text-white"
          >
            <Plus className="w-4 h-4" /> Add Freelancer
          </Button>
        }
      />
      <TableShell search={q} onSearch={setQ} placeholder="Search by name, email or mobile…" testId="admin-freelancers-search">
        {items === null ? <Loading /> : filtered.length === 0 ? <Empty msg="No freelancers yet. Click Add Freelancer to create one." /> : (
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <Th>Name</Th><Th>Email</Th><Th>Mobile</Th><Th>Status</Th><Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((u) => (
                <tr key={u.id} data-testid={`admin-freelancer-row-${u.id}`} className="hover:bg-slate-50">
                  <Td className="font-medium text-slate-900">{u.name}</Td>
                  <Td><a className="text-primary hover:underline" href={`mailto:${u.email}`}>{u.email}</a></Td>
                  <Td>{u.mobile || "—"}</Td>
                  <Td>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold border ${
                      u.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-slate-100 text-slate-600 border-slate-200"
                    }`}>
                      {u.is_active ? "Active" : "Disabled"}
                    </span>
                  </Td>
                  <Td className="text-right whitespace-nowrap">
                    <button
                      onClick={() => { setEditing(u); setOpen(true); }}
                      data-testid={`admin-freelancer-edit-${u.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold text-slate-700 hover:bg-slate-100"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleToggle(u)}
                      data-testid={`admin-freelancer-toggle-${u.id}`}
                      title={u.is_active ? "Disable" : "Enable"}
                      className={`ml-1 inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold hover:bg-slate-100 ${u.is_active ? "text-slate-700" : "text-emerald-700"}`}
                    >
                      {u.is_active ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </TableShell>

      <FreelancerFormDialog
        open={open}
        onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}
        user={editing}
        onSaved={() => { refresh(); setOpen(false); setEditing(null); }}
      />
    </div>
  );
}

// ---------- Shared subcomponents ----------
function PageHeader({ title, subtitle, right }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
      <div>
        <h1 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900 tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

function TableShell({ search, onSearch, placeholder, testId, rightSlot, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <div className="p-4 border-b border-slate-100 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={placeholder}
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            data-testid={testId}
            className="pl-9 h-10"
          />
        </div>
        {rightSlot}
      </div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 text-left text-xs font-semibold ${className}`}>{children}</th>;
}
function Td({ children, className = "", ...rest }) {
  return <td className={`px-4 py-3 ${className}`} {...rest}>{children}</td>;
}
function Loading() { return <div className="py-16 text-center text-slate-500 inline-flex items-center justify-center w-full gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>; }
function Empty({ msg }) { return <div className="py-16 text-center text-slate-500">{msg}</div>; }
function ErrorBlock({ msg }) { return <div className="p-6 rounded-2xl border border-destructive/30 bg-destructive/10 text-destructive">{msg}</div>; }
